import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { rewardService } from '../../services/reward';
import { getAssociates } from '../../services/associate';

const RewardTransaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [associateFilter, setAssociateFilter] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
             ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'N/A';
    }
  };

  const mapApiTransactionToLocal = (apiTransaction) => {
    const associate = apiTransaction.associate || {};
    
    return {
      id: apiTransaction._id || apiTransaction.id,
      transactionId: apiTransaction._id || 'N/A',
      userId: apiTransaction.userId || 'N/A',
      userType: apiTransaction.userType || 'N/A',
      orderId: apiTransaction.orderId || 'N/A',
      orderType: apiTransaction.orderType || 'N/A',
      points: apiTransaction.points || 0,
      status: apiTransaction.status === 'pending' ? 'Pending' : 
              apiTransaction.status === 'approved' ? 'Approved' : 
              apiTransaction.status === 'rejected' ? 'Rejected' : 'Pending',
      createdAt: apiTransaction.createdAt || '',
      updatedAt: apiTransaction.updatedAt || '',
      associateId: associate._id || 'N/A',
      associateName: associate.name || 'N/A',
      associatePhone: associate.phone || 'N/A',
      associateEmail: associate.email || 'N/A',
      associateAssociateId: associate.associateId || 'N/A',
      associateData: associate,
    };
  };

  const fetchAssociates = async () => {
    try {
      setLoadingAssociates(true);
      const response = await getAssociates({ page: 1, limit: 1000 });
      const associatesList = Array.isArray(response?.data) 
        ? response.data 
        : (response?.data?.docs || []);
      
      const mappedAssociates = associatesList.map((a) => ({
        id: a._id || a.id,
        _id: a._id || a.id,
        name: a.name || 'N/A',
        phone: a.phone || a.mobile || 'N/A',
        associateId: a.associateId || a._id,
      }));
      setAssociates(mappedAssociates);
    } catch (error) {
      console.error('Failed to fetch associates:', error);
      setAssociates([]);
    } finally {
      setLoadingAssociates(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      // Map status filter to API format
      const apiStatus = statusFilter === 'all' ? 'all' : 
                       statusFilter === 'Pending' ? 'pending' :
                       statusFilter === 'Approved' ? 'approved' :
                       statusFilter === 'Rejected' ? 'rejected' : 'all';
      
      // Get associate ID if filter is set
      const associateId = associateFilter === 'all' ? null : associateFilter;
      
      const data = await rewardService.getRewardTransactions(associateId, apiStatus);
      const mappedTransactions = Array.isArray(data) ? data.map(mapApiTransactionToLocal) : [];
      setTransactions(mappedTransactions);
    } catch (err) {
      console.error('Failed to fetch reward transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, associateFilter]);

  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    setViewDialogOpen(true);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Approved':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Rejected':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getOrderTypeStyle = (orderType) => {
    switch (orderType) {
      case 'booking':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'redeem':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchQuery === '' ||
      transaction.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.associateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.associatePhone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.associateEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(transaction.points).includes(searchQuery);
    return matchesSearch;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Reward Transactions
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTransactions}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by Order ID, Associate Name, Phone, Email or Points"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Associate</InputLabel>
                <Select
                  value={associateFilter}
                  onChange={(e) => {
                    setAssociateFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Associate"
                  disabled={loadingAssociates}
                >
                  <MenuItem value="all">All Associates</MenuItem>
                  {associates.map((associate) => (
                    <MenuItem key={associate.id} value={associate._id}>
                      {associate.name} ({associate.phone})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
            <CircularProgress size={50} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Associate</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Order Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Points</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Transaction Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((transaction, index) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {transaction.transactionId.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.associateName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {transaction.associateAssociateId} | {transaction.associatePhone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.orderId.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.orderType.charAt(0).toUpperCase() + transaction.orderType.slice(1)}
                            size="small"
                            style={getOrderTypeStyle(transaction.orderType)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {transaction.orderType === 'booking' ? (
                              <TrendingUpIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                            ) : (
                              <TrendingDownIcon sx={{ fontSize: 16, color: '#c62828' }} />
                            )}
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={transaction.orderType === 'booking' ? 'success.main' : 'error.main'}
                            >
                              {transaction.orderType === 'booking' ? '+' : '-'}{transaction.points.toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            size="small"
                            style={getStatusStyle(transaction.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transaction.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton color="info" size="small" onClick={() => handleView(transaction)}>
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No transactions found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && (
          <TablePagination
            // rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main' }} />
            Transaction Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Transaction ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.transactionId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedTransaction.status}
                  size="small"
                  style={getStatusStyle(selectedTransaction.status)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Associate Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Associate Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.associateName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Associate ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.associateAssociateId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.associatePhone}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.associateEmail}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Transaction Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Order ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.orderId}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Order Type
                </Typography>
                <Chip
                  label={selectedTransaction.orderType.charAt(0).toUpperCase() + selectedTransaction.orderType.slice(1)}
                  size="small"
                  style={getOrderTypeStyle(selectedTransaction.orderType)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Points
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ mb: 2, fontWeight: 600 }}
                  color={selectedTransaction.orderType === 'booking' ? 'success.main' : 'error.main'}
                >
                  {selectedTransaction.orderType === 'booking' ? '+' : '-'}{selectedTransaction.points.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  User Type
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedTransaction.userType.charAt(0).toUpperCase() + selectedTransaction.userType.slice(1)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {formatDate(selectedTransaction.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Updated Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {formatDate(selectedTransaction.updatedAt)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RewardTransaction;

